import { ChangeEvent, Dispatch, SetStateAction, useState, useEffect } from "react";
import TextField from "@mui/material/TextField";
import CloseIcon from "@mui/icons-material/Close";
import { Modal, Button, CardMedia, Input, CircularProgress, Select, MenuItem } from "@mui/material";
import { useAddCategoryMutation, useUploadMediaMutation, useUpdateCategoryMutation } from "../service/features/api";
import { toast } from "react-toastify";
import { VEHICLE_TYPE } from "../constants/enum";
import { getVehicleTypeLabel } from "../utils/vehicle";

interface ModalProps {
  open: boolean;
  onClose: () => void;
  setOpen: Dispatch<SetStateAction<boolean>>;
  parentId: string;
  subCategoryData?: any;
  parentVehicleTypes?: number | number[];
}

export default function AddSubCategory({
  open,
  onClose,
  setOpen,
  parentId,
  subCategoryData,
  parentVehicleTypes,
}: ModalProps) {
  const [name, setName] = useState("");
  const [nameAr, setNameAr] = useState("");
  const [image, setImage] = useState("");
  const [selectedVehicleTypes, setSelectedVehicleTypes] = useState<number[]>([]);
  const [errors, setErrors] = useState<any>({});
  const [isUploading, setIsUploading] = useState(false);

  const [addCategory, { isLoading: isAdding }] = useAddCategoryMutation();
  const [updateCategory, { isLoading: isUpdating }] = useUpdateCategoryMutation();
  const [uploadMedia] = useUploadMediaMutation();

  useEffect(() => {
    if (open) {
      if (subCategoryData) {
        setName(subCategoryData.name || "");
        setNameAr(subCategoryData.name_ar || "");
        setImage(subCategoryData.image || subCategoryData.icon || "");
        const vType = subCategoryData.type;
        setSelectedVehicleTypes(Array.isArray(vType) ? vType : (vType ? [vType] : []));
        setErrors({});
      } else {
        setName("");
        setNameAr("");
        setImage("");
        const initialTypes = Array.isArray(parentVehicleTypes)
          ? parentVehicleTypes
          : (parentVehicleTypes ? [parentVehicleTypes] : []);
        setSelectedVehicleTypes(initialTypes);
        setErrors({});
      }
    }
  }, [open, subCategoryData, parentVehicleTypes]);

  const handleFileUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files[0]) {
      const file = files[0];

      // Validate file type
      const allowedTypes = ["image/jpeg", "image/png", "image/jpg"];
      if (!allowedTypes.includes(file.type)) {
        toast.error("Format not supported (Only JPG, JPEG, and PNG)");
        return;
      }

      const formData = new FormData();
      formData.append("file", file);

      setIsUploading(true);
      try {
        const res = await uploadMedia(formData).unwrap();
        const uploadedUrl = res?.data?.url || res?.url || res?.data;
        if (uploadedUrl) {
          setImage(uploadedUrl);
          toast.success("Image uploaded successfully");
        }
      } catch (err: any) {
        toast.error(err?.data?.message || "Image upload failed");
      } finally {
        setIsUploading(false);
      }
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    let tempErrors: any = {};
    let isValid = true;

    if (!name.trim()) {
      tempErrors.name = "Sub-Category name is required";
      isValid = false;
    } else if (name.length > 40) {
      tempErrors.name = "Sub-Category Name cannot exceed 40 characters";
      isValid = false;
    }

    if (nameAr.trim() && nameAr.length > 40) {
      tempErrors.nameAr = "اسم الفئة الفرعية cannot exceed 40 characters";
      isValid = false;
    }

    if (selectedVehicleTypes.length === 0) {
      tempErrors.vehicleType = "At least one vehicle type is required";
      isValid = false;
    }

    setErrors(tempErrors);
    if (!isValid) return;

    const payload: any = {
      name: name,
      name_ar: nameAr,
      order: subCategoryData?.order || 1,
      type: selectedVehicleTypes,
    };

    if (image) {
      payload.image = image;
      payload.icon = image;
    }

    if (!subCategoryData) {
      if (!parentId) {
        toast.error("Parent category not found");
        return;
      }
      payload.parentId = parentId;
    } else {
      payload.parentId = parentId;
    }

    try {
      if (subCategoryData?._id) {
        await updateCategory({ id: subCategoryData._id, ...payload }).unwrap();
        toast.success("Sub-Category updated successfully");
      } else {
        await addCategory(payload).unwrap();
        toast.success("Sub-Category added successfully");
      }
      setOpen(false);
    } catch (err: any) {
      toast.error(err?.data?.message || "Operation failed");
    }
  };

  return (
    <Modal
      className="modal rejectReason_modal"
      open={open}
      onClose={onClose}
    >
      <div className="modal-dialog" style={{ maxWidth: 700, width: "100%" }}>
        <div className="modal-body">
          {/* MODAL TITLE */}
          <div className="modal_title">
            <h2>{subCategoryData ? "Edit Sub-Category (تعديل الفئة الفرعية)" : "Add Sub-Category (إضافة فئة فرعية)"}</h2>
            <button
              type="button"
              className="btn-close"
              onClick={() => {
                setErrors({});
                setOpen(false);
              }}
              aria-label="Close"
            >
              <CloseIcon />
            </button>
          </div>

          <form className="form" onSubmit={handleSave}>
            <div className="gap_p">
              {/* UPLOAD IMAGE */}
              <div className="control_group w_100">
                <label htmlFor="upload-subcategory-image">
                  Upload Image (تحميل الصورة)
                </label>

                {image ? (
                  <div className="upload_image no_radius">
                    <div className="upload_image_holder">
                      <figure>
                        <CardMedia component="img" image={image} alt="Sub-Category" />
                      </figure>
                      <CloseIcon
                        className="remove_img"
                        onClick={() => setImage("")}
                      />
                    </div>
                  </div>
                ) : (
                  <label
                    className="upload_image no_radius"
                    htmlFor="upload-subcategory-image"
                  >
                    <Input
                      sx={{ display: "none" }}
                      id="upload-subcategory-image"
                      type="file"
                      inputProps={{ accept: "image/png,image/jpeg" }}
                      onChange={handleFileUpload}
                      disabled={isUploading}
                    />
                    <span className="upload_image_holder">
                      <figure className="add_img">
                        {isUploading ? (
                          <CircularProgress size={24} />
                        ) : (
                          <>
                            <img
                              src="/static/images/placeholder.png"
                              alt="Upload"
                            />
                            <span className="upload_img_icon">
                              <img
                                src="/static/images/edit2_icon.svg"
                                alt="Edit"
                              />
                            </span>
                          </>
                        )}
                      </figure>
                    </span>
                  </label>
                )}
              </div>

              {/* SUB-CATEGORY NAME + SUB-CATEGORY NAME (ARABIC) */}
              <div style={{ display: "flex", gap: 16, width: "100%" }}>
                <div className="control_group" style={{ flex: 1 }}>
                  <label htmlFor="subcategory-name">Sub-Category Name</label>
                  <TextField
                    id="subcategory-name"
                    placeholder="Enter Name"
                    fullWidth
                    hiddenLabel
                    value={name}
                    onChange={(e) => {
                      const input = e.target.value;
                      if (input.length > 0) {
                        setName(input.charAt(0).toUpperCase() + input.slice(1));
                      } else {
                        setName(input);
                      }
                      if (errors.name) setErrors({ ...errors, name: "" });
                    }}
                    error={!!errors.name}
                    helperText={errors.name}
                    inputProps={{ maxLength: 40 }}
                  />
                </div>

                <div className="control_group" style={{ flex: 1 }}>
                  <label htmlFor="subcategory-name-ar">اسم الفئة الفرعية</label>
                  <TextField
                    id="subcategory-name-ar"
                    placeholder="أدخل الاسم"
                    fullWidth
                    hiddenLabel
                    value={nameAr}
                    onChange={(e) => {
                      setNameAr(e.target.value);
                      if (errors.nameAr) setErrors({ ...errors, nameAr: "" });
                    }}
                    error={!!errors.nameAr}
                    helperText={errors.nameAr}
                    inputProps={{ maxLength: 40 }}
                  />
                </div>
              </div>

              {/* VEHICLE TYPE */}
              <div className="control_group">
                <label htmlFor="VehicleType">Vehicle Type (نوع المركبة)</label>
                <Select
                  multiple
                  fullWidth
                  hiddenLabel
                  displayEmpty
                  value={selectedVehicleTypes}
                  onChange={(event) => {
                    const value = event.target.value as number[];
                    setSelectedVehicleTypes(value);
                    if (errors.vehicleType) setErrors({ ...errors, vehicleType: "" });
                  }}
                  disabled={true}
                  renderValue={(selected) => {
                    if (selected.length === 0) return "Select Vehicle Type";
                    return selected.map(val =>
                      getVehicleTypeLabel(val)
                    ).join(", ");
                  }}
                  error={!!errors.vehicleType}
                >
                  <MenuItem value="" disabled>Select Vehicle Type</MenuItem>
                  {Object.keys(VEHICLE_TYPE)
                    .filter(k => isNaN(Number(k)))
                    .map((key) => (
                      <MenuItem key={key} value={(VEHICLE_TYPE as any)[key]}>
                        {getVehicleTypeLabel((VEHICLE_TYPE as any)[key])}
                      </MenuItem>
                    ))}
                </Select>
                {errors.vehicleType && <div className="error_text" style={{ color: 'red', fontSize: '12px', marginTop: '4px' }}>{errors.vehicleType}</div>}
              </div>
            </div>

            {/* FOOTER BUTTON */}
            <div className="footer_btn_flex">
              <Button
                type="submit"
                className="btn btn_primary"
                disabled={isAdding || isUpdating || isUploading}
              >
                {isAdding || isUpdating ? "Saving... (توفير...)" : "Save (يحفظ)"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
}
